import React, { useCallback, useContext, useMemo } from 'react';
import {
  createBlockquotePlugin,
  createBoldPlugin,
  createCodeBlockPlugin,
  createComboboxPlugin,
  createEventEditorPlugin,
  createItalicPlugin,
  createLinkPlugin,
  createListPlugin,
  createSoftBreakPlugin,
  createStrikethroughPlugin,
  createUnderlinePlugin,
  PlateProvider,
} from '@udecode/plate';
import get from 'lodash/get';
import set from 'lodash/set';
import styled from 'styled-components';

import createMemorySelectorPlugin from './plugins/memorySelectorPlugin/createMemorySelectorPlugin';
import { EditorFloatingLink } from 'src/presentation/components/pages/chat/components/EditorFloatingLink';
import EditorToolbar from './EditorToolbar';
import { FieldContained } from '@ad-scripts/presentation/components/common/textarea_field';
import FormContext from 'src/presentation/contexts/FormContext';
import { isUrlValid } from '@ad-scripts/presentation/components/lib/linkify_it';
import MemorySelectorCombobox from './plugins/memorySelectorPlugin/MemorySelectorCombobox';
import { serializeHtml } from './utils/serialize_html';
import TextEditor from '@ad-scripts/presentation/components/text_editor_new';
import { useDebounced } from '@ad-scripts/presentation/components/hooks/debounce_hooks';
import useEditorRef, { getNormalizedUrl } from 'src/presentation/components/pages/chat/components/useEditorRef';
import useInitialValue from '@ad-scripts/presentation/components/text_editor_new/hooks/use_initial_value';
import { withLink } from 'src/presentation/components/pages/chat/components/withLink';

const linkPlugin = createLinkPlugin({
  renderAfterEditable: EditorFloatingLink,
  withOverrides: withLink,
  options: {
    // Automatically transform things that look like links into actual links. Maybe not desired but..
    getUrlHref: getNormalizedUrl,
    isUrl(text) {
      return isUrlValid(text) || (text.startsWith('{{') && text.endsWith('}}'));
    },
  },
});

export default function HtmlEditor({
  attr,
  description,
  disabled,
  placeholder,
  prompt,
}: {
  attr: string;
  description?: string;
  disabled: boolean;
  placeholder?: string;
  prompt?: string;
}) {
  const { workingState, updateState, onBlur } = useContext(FormContext);

  const handleBlur = useCallback(
    editor => {
      const changes = set({}, attr, serializeHtml(editor, true));
      onBlur && onBlur({ currentState: changes });
    },
    [attr, onBlur]
  );

  const extraPlugins = useMemo(
    () => [
      linkPlugin,
      createListPlugin(),
      createBoldPlugin(),
      createItalicPlugin(),
      createUnderlinePlugin(),
      createSoftBreakPlugin(),
      createCodeBlockPlugin(),
      createStrikethroughPlugin(),
      createBlockquotePlugin(),
      createComboboxPlugin(),

      createMemorySelectorPlugin(),

      createEventEditorPlugin({
        handlers: {
          onBlur: editor => () => handleBlur(editor),
        },
      }),
    ],
    [handleBlur]
  );

  const editorRef = useEditorRef(attr, extraPlugins);

  const html = get(workingState, attr, '');
  const htmlValue = html.replace(/(?<!href="){{(.*?)}}/g, '<variable data-value="$1">{{$1}}</variable>');

  const initialValue = useInitialValue(htmlValue, editorRef.current);

  const onChange = useCallback(() => {
    const changes = set({}, attr, serializeHtml(editorRef.current, true));
    updateState(changes, { replaceArrayValues: true });
  }, [editorRef, updateState, attr]);

  const debouncedOnChange = useDebounced(onChange, 500, {});

  return (
    <FieldContained
      attr={attr}
      description={description}
      label={prompt}
      control={
        <StyledHtmlEditor>
          <PlateProvider
            id={attr}
            editor={editorRef.current}
            initialValue={initialValue}
            plugins={editorRef.current.plugins}
            readOnly={disabled}
          >
            <EditorToolbar attr={attr} editorRef={editorRef} disabled={disabled} />

            <MemorySelectorCombobox />

            <StyledTextEditor
              className="text-editor"
              editorRef={editorRef}
              onChange={debouncedOnChange}
              readOnly={disabled}
              width="100%"
              placeholder={placeholder}
            />
          </PlateProvider>
        </StyledHtmlEditor>
      }
    />
  );
}

const StyledHtmlEditor = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => (p.hasError ? p.theme.colors.red400 : p.theme.colors.gray400)};
  border-radius: 4px;
  margin-top: 8px;
  margin-bottom: 8px;
  position: relative;
  width: 100%;

  & [data-slate-editor='true'] {
    padding: 8px;
  }

  &:focus-within {
    border-color: ${p => p.theme.colors.green400};
  }

  & .slate-ToolbarButton-active,
  & .slate-ToolbarButton:hover {
    color: ${p => p.theme.colors.green400};
  }
`;

const StyledTextEditor = styled(TextEditor)`
  line-height: ${p => p.theme.lineHeight.base};
`;

export const Item = styled.span`
  align-items: center;
  border-radius: 4px;
  border: 1px solid ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.gray900};
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  height: 25px;
  justify-content: center;
  margin-bottom: 0;
  text-align: center;
  width: 25px;

  &.disabled {
    cursor: default;
  }
  &:hover:not(.disabled) {
    border: 1px solid ${p => p.theme.colors.gray200};
  }
  & .placeholderIcon {
    fill: ${p => p.theme.colors.gray900};
    height: 14px;
    width: 14px;
  }
`;
